import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getKitBySlug, kits, categories, bundles } from '../data/products';

const KitDetailPage = () => {
  const { slug } = useParams();
  const { openCheckout } = useAuth();
  const kit = getKitBySlug(slug);

  if (!kit) {
    return <Navigate to="/kits" replace />;
  }

  // Find related kits (same category, excluding current)
  const relatedKits = kits
    .filter(k => k.category === kit.category && k.id !== kit.id)
    .slice(0, 3);

  // Find bundles that include this kit
  const containingBundles = bundles.filter(b => b.includedKits.includes(kit.id));

  return (
    <div className="kit-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <Link to="/kits">Interview Kits</Link>
          <span className="separator">/</span>
          <span className="current">{kit.shortName}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="kit-detail-hero">
        <div className="container">
          <div className="kit-detail-grid">
            <div className="kit-detail-info">
              <div className="kit-detail-badges">
                <span className="category-badge">
                  {categories[kit.category]?.icon} {categories[kit.category]?.name}
                </span>
              </div>
              <h1 className="kit-detail-title">
                <span className="kit-detail-icon">{kit.icon}</span>
                {kit.name}
              </h1>
              <p className="kit-detail-description">{kit.description}</p>

              <div className="kit-detail-stats">
                <div className="detail-stat">
                  <span className="stat-value">{kit.questionCount}</span>
                  <span className="stat-label">Questions</span>
                </div>
                <div className="detail-stat">
                  <span className="stat-value">{kit.topics.length}</span>
                  <span className="stat-label">Topics</span>
                </div>
                <div className="detail-stat">
                  <span className="stat-value">PDF</span>
                  <span className="stat-label">Format</span>
                </div>
              </div>

              <div className="kit-detail-features">
                <h3>What you'll get:</h3>
                <ul className="features-list">
                  {kit.highlights.map((highlight, i) => (
                    <li key={i}>
                      <span className="check-icon">✓</span>
                      {highlight}
                    </li>
                  ))}
                  <li>
                    <span className="check-icon">✓</span>
                    Instant download access
                  </li>
                  <li>
                    <span className="check-icon">✓</span>
                    Free lifetime updates
                  </li>
                </ul>
              </div>
            </div>

            <div className="kit-detail-purchase">
              <div className="purchase-card">
                <div className="purchase-price">
                  <span className="original-price">₹{kit.price * 10}</span>
                  <div className="current-price">
                    <span className="currency">₹</span>
                    <span className="amount">{kit.price}</span>
                  </div>
                </div>
                <p className="purchase-note">One-time payment • Instant access</p>

                <button
                  onClick={() => openCheckout({ id: kit.id, name: kit.name, price: kit.price })}
                  className="btn btn-primary btn-large btn-full"
                >
                  Get Kit - ₹{kit.price}
                </button>


                <div className="purchase-includes">
                  <p className="includes-label">This kit includes:</p>
                  <ul>
                    <li>✓ {kit.questionCount} curated interview questions</li>
                    <li>✓ Detailed explanations & code examples</li>
                    <li>✓ PDF format for easy reading</li>
                    <li>✓ Free updates forever</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="kit-topics-section">
        <div className="container">
          <h2 className="section-title">Topics Covered</h2>
          <p className="section-subtitle">
            This kit covers all essential {kit.shortName} concepts asked in interviews
          </p>
          <div className="topics-grid">
            {kit.topics.map((topic, i) => (
              <div key={i} className="topic-card">
                <span className="topic-number">{i + 1}</span>
                <span className="topic-name">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Questions Preview */}
      <section className="sample-questions-section">
        <div className="container">
          <h2 className="section-title">Sample Questions</h2>
          <p className="section-subtitle">Get a taste of what's inside</p>
          <div className="sample-questions">
            <div className="sample-question">
              <span className="sample-badge">Sample</span>
              <p className="sample-text">
                Questions in this kit are designed to test your understanding of {kit.topics[0]},
                {kit.topics[1]}, and other key {kit.shortName} concepts that interviewers frequently ask about.
              </p>
            </div>
            <div className="preview-blur">
              <p>Get the full kit to access all {kit.questionCount} questions with detailed answers</p>
              <button
                onClick={() => openCheckout({ id: kit.id, name: kit.name, price: kit.price })}
                className="btn btn-primary"
              >
                Get Full Access
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Save with Bundles */}
      {containingBundles.length > 0 && (
        <section className="bundle-upsell-section">
          <div className="container">
            <h2 className="section-title">Save More with Bundles</h2>
            <p className="section-subtitle">This kit is included in these bundles</p>
            <div className="bundle-upsell-grid">
              {containingBundles.slice(0, 3).map(bundle => (
                <div key={bundle.id} className="bundle-upsell-card">
                  <div className="upsell-header">
                    <span className="upsell-icon">{bundle.icon}</span>
                    <h3>{bundle.name}</h3>
                  </div>
                  <p className="upsell-description">{bundle.kitCount} kits included</p>
                  <div className="upsell-price">
                    <span className="original">₹{bundle.originalPrice}</span>
                    <span className="current">₹{bundle.price}</span>
                    <span className="savings-badge">Save ₹{bundle.savings}</span>
                  </div>
                  <Link to={`/bundles/${bundle.slug}`} className="btn btn-outline btn-full">
                    View Bundle
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Kits */}
      {relatedKits.length > 0 && (
        <section className="related-kits-section">
          <div className="container">
            <h2 className="section-title">Related Kits</h2>
            <p className="section-subtitle">Other {categories[kit.category]?.name} kits you might like</p>
            <div className="related-kits-grid">
              {relatedKits.map(relKit => (
                <div key={relKit.id} className="related-kit-card">
                  <div className="related-kit-header">
                    <span className="related-kit-icon">{relKit.icon}</span>
                    <h3>{relKit.name}</h3>
                  </div>
                  <p className="related-kit-questions">{relKit.questionCount} Questions</p>
                  <div className="related-kit-footer">
                    <span className="related-kit-price">₹{relKit.price}</span>
                    <Link to={`/kits/${relKit.slug}`} className="btn btn-outline btn-small">
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="kit-final-cta">
        <div className="container">
          <div className="final-cta-content">
            <h2>Ready to ace your {kit.shortName} interview?</h2>
            <p>Get instant access to {kit.questionCount} curated questions</p>
            <button
              onClick={() => openCheckout({ id: kit.id, name: kit.name, price: kit.price })}
              className="btn btn-primary btn-large"
            >
              Get {kit.name} - ₹{kit.price}
            </button>
            <p className="guarantee-text">Instant download • PDF delivered to your email</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KitDetailPage;
